import React, { useState } from "react";
import { useAuthStore } from "@/lib/auth";
import { Logo } from "@/components/layout/AppShell";
import { Button, Input, Field, Spinner } from "@/components/ui";
import { supabase } from "@/lib/supabase";

export default function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { signIn, error } = useAuthStore();

  // T-198 — self-service forgot-password.
  // Uses supabase.auth.resetPasswordForEmail() (the public auth method, well-tested
  // in anon-client context — distinct from the broken admin path T-196 fixed).
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email, password);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgotPassword() {
    setResetMessage(null);
    if (!email.trim()) {
      setResetMessage({ kind: "err", text: "Type your email above first, then click Forgot password again." });
      return;
    }
    setResetSubmitting(true);
    try {
      const { error: rpfeErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (rpfeErr) {
        // Anti-enumeration: Supabase normally returns success regardless. If we DO
        // see an error, it's typically rate limit ("Email rate limit exceeded") or a
        // configuration issue — surface it.
        throw rpfeErr;
      }
      setResetMessage({
        kind: "ok",
        text: `If ${email.trim()} is registered, a reset link has been sent. Check your inbox (and junk on first send).`,
      });
    } catch (err: any) {
      setResetMessage({ kind: "err", text: err?.message ?? "Couldn't send reset email — try again." });
    } finally {
      setResetSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-ink text-text flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Brand block */}
        <div className="flex flex-col items-center mb-10">
          <Logo size={40} showText={false} />
          <div className="mt-4 text-text-muted text-[11px] uppercase tracking-[0.2em]">Operations Platform</div>
        </div>

        <form onSubmit={handleSubmit} className="card p-6 space-y-4">
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@coretelgroup.com"
              autoComplete="email"
              required
              autoFocus
            />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          {error && (
            <div className="text-danger text-[12px] bg-danger/10 border border-danger/20 rounded px-3 py-2">
              {error}
            </div>
          )}

          <Button type="submit" size="lg" disabled={submitting} className="w-full justify-center">
            {submitting ? <Spinner size={14} /> : "Sign in"}
          </Button>

          {/* T-198 — self-service forgot password */}
          <div className="text-right">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetSubmitting}
              className="text-[11px] text-accent hover:underline disabled:opacity-50"
            >
              {resetSubmitting ? "Sending reset link…" : "Forgot password?"}
            </button>
          </div>

          {resetMessage && (
            <div
              className={`text-[12px] rounded px-3 py-2 ${
                resetMessage.kind === "ok"
                  ? "bg-accent/10 border border-accent/20 text-text"
                  : "bg-danger/10 border border-danger/20 text-danger"
              }`}
            >
              {resetMessage.text}
            </div>
          )}
        </form>

        <div className="mt-6 text-center text-text-muted text-[11px]">
          Type your email + click <span className="text-text">Forgot password?</span> to receive a reset link.
          <br />
          2FA is required on first login.
        </div>
      </div>
    </div>
  );
}
